import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { teamDoc } from '../../../firebase/collections';
import type { Team } from '../types';

type CachedTeam = Team & { id: string };

/** Session cache so remounts / Activity re-shows don't spinner-flash. */
const userTeamCache = new Map<string, CachedTeam | null>();

export function useUserTeam(teamId: string | null | undefined) {
  const cached = teamId ? userTeamCache.get(teamId) : undefined;
  const [team, setTeam] = useState<CachedTeam | null>(() =>
    teamId ? (cached !== undefined ? cached : null) : null,
  );
  const [loading, setLoading] = useState(() => Boolean(teamId) && cached === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamId) {
      setTeam(null);
      setLoading(false);
      setError(null);
      return;
    }

    const warm = userTeamCache.get(teamId);
    if (warm !== undefined) {
      setTeam(warm);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const unsub = onSnapshot(
      teamDoc(teamId),
      (snap) => {
        if (!snap.exists()) {
          userTeamCache.set(teamId, null);
          setTeam(null);
          setError('Team not found.');
        } else {
          const next = { ...(snap.data() as Team), id: snap.id };
          userTeamCache.set(teamId, next);
          setTeam(next);
          setError(null);
        }
        setLoading(false);
      },
      (err) => {
        setTeam(null);
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId]);

  return { team, loading, error };
}
